// Match-config section updaters. Each helper merges a patch into a sub-config
// while preserving sibling keys and applying defaults for missing fields.
// Extracted from MatchSettingsTab.tsx to keep the component file focused on layout.

import type { MatchConfigOverrides } from '../types/MatchConfigOverrides.js';

export function updateVictory(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['victory']>,
) {
  return {
    victory: {
      minEmpireTick: config.victory?.minEmpireTick ?? 35,
      sustainEmpireTicks: config.victory?.sustainEmpireTicks ?? 5,
      economyGold: config.victory?.economyGold ?? 0,
      economyTradeRoutes: config.victory?.economyTradeRoutes ?? 0,
      scienceKnowledge: config.victory?.scienceKnowledge ?? 0,
      faithTotal: config.victory?.faithTotal ?? 0,
      faithTemples: config.victory?.faithTemples ?? 0,
      diplomacyHonor: config.victory?.diplomacyHonor ?? 0,
      teamPowerThresholdPct: config.victory?.teamPowerThresholdPct ?? 0,
      ...patch,
    },
  };
}

export function updateAI(config: MatchConfigOverrides, patch: Partial<MatchConfigOverrides['ai']>) {
  return {
    ai: {
      shuffleInterval: config.ai?.shuffleInterval ?? 0,
      aggressionRange: config.ai?.aggressionRange ?? [0.1, 0.9],
      cautionRange: config.ai?.cautionRange ?? [0.1, 0.9],
      ...patch,
    },
  };
}

export function updateGameSpeed(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['gameSpeed']>,
) {
  return {
    gameSpeed: {
      incomeInterval: config.gameSpeed?.incomeInterval ?? 10,
      empireInterval: config.gameSpeed?.empireInterval ?? 10,
      deliberationInterval: config.gameSpeed?.deliberationInterval ?? 5,
      movementResetInterval: config.gameSpeed?.movementResetInterval ?? 10,
      slowIncomeInterval: config.gameSpeed?.slowIncomeInterval ?? 20,
      aiMoveInterval: config.gameSpeed?.aiMoveInterval ?? 5,
      ...patch,
    },
  };
}

export function updateSiege(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['siege']>,
) {
  return {
    siege: {
      evalInterval: config.siege?.evalInterval ?? 10,
      moodPenalty: config.siege?.moodPenalty ?? 2,
      loyaltyPenaltyInterval: config.siege?.loyaltyPenaltyInterval ?? 3,
      loyaltyPenalty: config.siege?.loyaltyPenalty ?? 1,
      guardSiegeDamage: config.siege?.guardSiegeDamage ?? 2,
      popLoss: config.siege?.popLoss ?? 1,
      surrenderMoodThreshold: config.siege?.surrenderMoodThreshold ?? 10,
      surrenderLoyaltyThreshold: config.siege?.surrenderLoyaltyThreshold ?? 10,
      surrenderMinSiegeTicks: config.siege?.surrenderMinSiegeTicks ?? 30,
      surrenderCapitalMoodThreshold: config.siege?.surrenderCapitalMoodThreshold ?? 5,
      surrenderCapitalLoyaltyThreshold: config.siege?.surrenderCapitalLoyaltyThreshold ?? 5,
      surrenderCapitalMinSiegeTicks: config.siege?.surrenderCapitalMinSiegeTicks ?? 60,
      ...patch,
    },
  };
}

export function updateEspionage(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['espionage']>,
) {
  return {
    espionage: {
      intelDeliveryDepth1: config.espionage?.intelDeliveryDepth1 ?? 3,
      intelDeliveryDepth2: config.espionage?.intelDeliveryDepth2 ?? 5,
      intelDeliveryDepth3: config.espionage?.intelDeliveryDepth3 ?? 8,
      detectionChanceAdjacent: config.espionage?.detectionChanceAdjacent ?? 30,
      detectionChance2Hex: config.espionage?.detectionChance2Hex ?? 20,
      detectionFailedHit: config.espionage?.detectionFailedHit ?? 50,
      bodyguardBlockChance: config.espionage?.bodyguardBlockChance ?? 40,
      bodyguardStrength: config.espionage?.bodyguardStrength ?? 1,
      intelBoostAuraRadius: config.espionage?.intelBoostAuraRadius ?? 3,
      intelBoostSpeedMultiplier: config.espionage?.intelBoostSpeedMultiplier ?? 2,
      spyNetworkIncrement: config.espionage?.spyNetworkIncrement ?? 1,
      crisisTickInterval: config.espionage?.crisisTickInterval ?? 10,
      revoltMoodThreshold: config.espionage?.revoltMoodThreshold ?? 20,
      revoltMoodChancePct: config.espionage?.revoltMoodChancePct ?? 70,
      defectionMoodChancePct: config.espionage?.defectionMoodChancePct ?? 20,
      defectionLoyaltyThreshold: config.espionage?.defectionLoyaltyThreshold ?? 30,
      defectionLoyaltyChancePct: config.espionage?.defectionLoyaltyChancePct ?? 60,
      ...patch,
    },
  };
}

export function updateSacrifice(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['sacrifice']>,
) {
  return {
    sacrifice: {
      minPopulation: config.sacrifice?.minPopulation ?? 5000,
      popFractionNumerator: config.sacrifice?.popFractionNumerator ?? 1,
      popFractionDenominator: config.sacrifice?.popFractionDenominator ?? 10,
      faithReturnNumerator: config.sacrifice?.faithReturnNumerator ?? 1,
      faithReturnDenominator: config.sacrifice?.faithReturnDenominator ?? 2,
      slaveFaithReturnNumerator: config.sacrifice?.slaveFaithReturnNumerator ?? 3,
      slaveFaithReturnDenominator: config.sacrifice?.slaveFaithReturnDenominator ?? 5,
      cooldownTicks: config.sacrifice?.cooldownTicks ?? 5,
      moodPenalty: config.sacrifice?.moodPenalty ?? -15,
      loyaltyPenalty: config.sacrifice?.loyaltyPenalty ?? -10,
      faithThresholdCap: config.sacrifice?.faithThresholdCap ?? 100,
      ...patch,
    },
  };
}

export function updateCombat(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['combat']>,
) {
  return {
    combat: {
      surrenderHpThresholdPct: config.combat?.surrenderHpThresholdPct ?? 20,
      surrenderConversionCostPct: config.combat?.surrenderConversionCostPct ?? 25,
      ...patch,
    },
  };
}

export function updateEconomy(
  config: MatchConfigOverrides,
  patch: Partial<MatchConfigOverrides['economy']>,
) {
  return {
    economy: {
      baseFoodPerCity: config.economy?.baseFoodPerCity ?? 10,
      cityLootGoldPerPop: config.economy?.cityLootGoldPerPop ?? 10,
      cityLootIndustryPerTier: config.economy?.cityLootIndustryPerTier ?? 5,
      ...patch,
    },
  };
}
